import { useState, useEffect, useMemo, useCallback } from "react";
import { CUSTOMER_ALIAS_KEY, resolveCustomer } from "./customerAlias";

// ══════════════════════════════════════════════════════════
//  #CRM_V1 (2026-09-23) — Bill: "build out a CRM to track customer activity, sales contacts that we
//  can also load... a data base of customers to contact and previous jobs we completed in the past."
//  Built for Gary Wilson's new CRM role; lives in Estimating (Customers tab) AND as its own top-level
//  'crm' tab — same component, two doors. Editing is gated by the 'crm' column in the Access Matrix,
//  never by an email list in code.
//
//  Customer universe = Revenue invoice lines (all years on file) + Job Cost cost-history + Job List
//  + manually added Prospects. Names run through the shared customer alias map, so a merge done in
//  the Alias tool shows up here too.
//
//  Storage (KV, same sg/ss as the rest of the app):
//    crm-contacts   [{id, customer, name, title, phone, email, notes, by, at}]
//    crm-activity   [{id, customer, contactId, type, date, note, nextDate, nextNote, done, by, at}]
//    crm-prospects  [{id, name, city, source, by, at}]
//    crm-migrated-notes  true once the old Estimating 'customer-notes' cards were copied in (#EST_CRM_V1)
//  Every write re-reads the key first so two people saving at once don't clobber each other.
// ══════════════════════════════════════════════════════════

const K = { bg:'#0A0E1A', card:'#111827', cardHi:'#1B2333', line:'#27304A', text:'#EAF1FF', muted:'#8C9AB8',
  accent:'#E7B73B', green:'#34D399', red:'#F87171', amber:'#FBBF24', blue:'#5B8DEF' };
const KEY_CONTACTS = 'crm-contacts', KEY_ACTIVITY = 'crm-activity', KEY_PROSPECTS = 'crm-prospects', KEY_MIGRATED = 'crm-migrated-notes';
const ACT_TYPES = ['Call', 'Email', 'Visit', 'Meeting', 'Bid', 'Note'];
const ACT_CLR = { Call:K.blue, Email:'#22D3EE', Visit:K.green, Meeting:'#A78BFA', Bid:K.accent, Note:K.muted };

const stripCP = s => String(s||'').toUpperCase().replace(/[\s-]+/g,'').replace(/^CP/,'').replace(/CP$/,'');
const _num = v => { const n = Number(String(v ?? '').replace(/[$,\s]/g,'')); return isFinite(n) ? n : 0; };
const todayISO = () => { const d = new Date(); return new Date(d.getTime() - d.getTimezoneOffset()*60000).toISOString().slice(0,10); };
const addDaysISO = (iso, n) => { const d = new Date(iso + 'T12:00:00'); d.setDate(d.getDate() + n); return d.toISOString().slice(0,10); };
const toISO = (s) => {
  s = String(s||'').trim(); if (!s) return null;
  let m = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/);
  if (m) { let y = +m[3]; if (y < 100) y += 2000; return `${y}-${String(m[1]).padStart(2,'0')}-${String(m[2]).padStart(2,'0')}`; }
  m = s.match(/^(\d{4})-(\d{2})-(\d{2})/); if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  return null;
};
const fmtDate = iso => { if (!iso) return '—'; const [y,m,d] = iso.split('-'); return `${+m}/${+d}/${String(y).slice(2)}`; };
const daysSince = iso => iso ? Math.floor((Date.parse(todayISO()) - Date.parse(iso)) / 86400000) : null;
const fmtPhone = p => { const d = String(p||'').replace(/\D/g,''); return d.length === 10 ? `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}` : (p||''); };
const money = n => '$' + Math.round(Number(n)||0).toLocaleString();
const newId = p => p + '-' + Date.now().toString(36) + Math.random().toString(36).slice(2,6);
const nameFromUpn = upn => String(upn||'').split('@')[0].split(/[._-]+/).filter(Boolean).map(x => x[0].toUpperCase() + x.slice(1)).join(' ');

// Plain CSV parser — handles quoted fields, embedded commas/quotes, CRLF.
function parseCSV(text) {
  const rows = []; let row = [], cur = '', q = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (q) { if (ch === '"') { if (text[i+1] === '"') { cur += '"'; i++; } else q = false; } else cur += ch; }
    else if (ch === '"') q = true;
    else if (ch === ',') { row.push(cur); cur = ''; }
    else if (ch === '\n' || ch === '\r') { if (ch === '\r' && text[i+1] === '\n') i++; row.push(cur); rows.push(row); row = []; cur = ''; }
    else cur += ch;
  }
  if (cur !== '' || row.length) { row.push(cur); rows.push(row); }
  return rows.filter(r => r.some(c => String(c).trim() !== ''));
}
const csvCell = v => { const s = String(v ?? ''); return /[",\n\r]/.test(s) ? '"' + s.replace(/"/g,'""') + '"' : s; };
const downloadCSV = (name, rows) => {
  const blob = new Blob([rows.map(r => r.map(csvCell).join(',')).join('\r\n')], { type:'text/csv' });
  const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = name; a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 2000);
};

const inp = { fontSize:12, padding:'6px 8px', border:`1px solid ${K.line}`, borderRadius:6, background:K.card, color:K.text, minWidth:0, fontFamily:'inherit' };
const btn = { fontSize:11.5, fontWeight:800, padding:'6px 11px', borderRadius:7, border:`1px solid ${K.line}`, background:K.cardHi, color:K.text, cursor:'pointer', whiteSpace:'nowrap' };
const btnGold = { ...btn, background:K.accent, color:'#0A0E1A', border:`1px solid ${K.accent}` };
const lbl = { fontSize:10.5, fontWeight:800, color:K.muted, textTransform:'uppercase', letterSpacing:.4 };
const card = { background:K.card, border:`1px solid ${K.line}`, borderRadius:12, padding:14 };

export default function CrmPanel({ jobs, sg, ss, canEdit, upn, custAlias: aliasProp, embedded }) {
  const [contacts, setContacts] = useState([]);
  const [activity, setActivity] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [aliasLocal, setAliasLocal] = useState({});
  const [revInput, setRevInput] = useState([]);
  const [costRows, setCostRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');     // all | customers | prospects | due | winback
  const [sortBy, setSortBy] = useState('rev');     // rev | lastJob | lastTouch | name
  const [sel, setSel] = useState(null);
  const [msg, setMsg] = useState('');
  const [showAddProspect, setShowAddProspect] = useState(false);
  const [pForm, setPForm] = useState({ name:'', city:'', source:'' });
  const [cForm, setCForm] = useState(null);        // contact being added/edited
  const [aForm, setAForm] = useState({ type:'Call', date:todayISO(), contactId:'', note:'', nextDate:'', nextNote:'' });
  const [imp, setImp] = useState(null);            // { contacts:[], prospects:[], skipped:n, file }

  const alias = aliasProp || aliasLocal;
  const by = nameFromUpn(upn) || upn || '';
  const canon = useCallback(n => resolveCustomer(alias, n) || String(n||'').trim(), [alias]);

  const loadCrm = useCallback(async () => {
    try { const c = await sg(KEY_CONTACTS); setContacts(Array.isArray(c) ? c : []); } catch {}
    try { const a = await sg(KEY_ACTIVITY); setActivity(Array.isArray(a) ? a : []); } catch {}
    try { const p = await sg(KEY_PROSPECTS); setProspects(Array.isArray(p) ? p : []); } catch {}
  }, [sg]);

  useEffect(() => { let dead = false; (async () => {
    try {
      const cy = new Date().getFullYear();
      let ri = await sg('revenue-input'); ri = Array.isArray(ri) ? ri.slice() : [];
      for (let y = cy - 1; y >= cy - 12; y--) { try { const h = await sg(`revenue-${y}`); if (Array.isArray(h)) ri = ri.concat(h); } catch {} }
      if (!dead) setRevInput(ri);
    } catch {}
    try { const ch = await sg('cost-history'); const arr = Array.isArray(ch) ? ch : (ch && Array.isArray(ch.jobs) ? ch.jobs : []); if (!dead) setCostRows(arr); } catch {}
    if (!aliasProp) { try { const al = await sg(CUSTOMER_ALIAS_KEY); if (!dead && al && typeof al === 'object' && !Array.isArray(al)) setAliasLocal(al); } catch {} }
    await loadCrm();
    if (!dead) setLoaded(true);
  })(); return () => { dead = true; }; }, [sg]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pick up other people's edits when the tab regains focus (Gary + estimators working the same list).
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === 'visible') loadCrm(); };
    window.addEventListener('focus', loadCrm); document.addEventListener('visibilitychange', onVis);
    return () => { window.removeEventListener('focus', loadCrm); document.removeEventListener('visibilitychange', onVis); };
  }, [loadCrm]);

  // Fresh read → change → save, so a concurrent save by someone else isn't wiped out.
  const mutate = useCallback(async (key, fn, setter) => {
    let base = [];
    try { const fresh = await sg(key); if (Array.isArray(fresh)) base = fresh; } catch {}
    const next = fn(base);
    setter(next);
    try { await ss(key, next); } catch (e) { setMsg('Could not save: ' + (e.message || e)); }
    return next;
  }, [sg, ss]);

  // One-time copy of the old Estimating Dashboard contact cards (#EST_CRM_V1 'customer-notes') into
  // the CRM, so nothing typed there is stranded. Runs once, for an editor, then sets a flag.
  useEffect(() => { if (!loaded || !canEdit) return; (async () => {
    try {
      if (await sg(KEY_MIGRATED)) return;
      const cn = await sg('customer-notes');
      if (!cn || typeof cn !== 'object' || Array.isArray(cn)) { await ss(KEY_MIGRATED, true); return; }
      const addC = [], addA = [];
      Object.entries(cn).forEach(([cust, v]) => {
        if (!v) return; const c = canon(cust);
        if (v.contact || v.phone || v.email) addC.push({ id:newId('c'), customer:c, name:v.contact||'', title:'', phone:v.phone||'', email:v.email||'', notes:'', by:'(from Estimating card)', at:new Date().toISOString() });
        if (v.notes) addA.push({ id:newId('a'), customer:c, contactId:'', type:'Note', date:todayISO(), note:v.notes, nextDate:'', nextNote:'', done:false, by:'(from Estimating card)', at:new Date().toISOString() });
      });
      if (addC.length) await mutate(KEY_CONTACTS, b => [...b, ...addC], setContacts);
      if (addA.length) await mutate(KEY_ACTIVITY, b => [...b, ...addA], setActivity);
      await ss(KEY_MIGRATED, true);
    } catch {}
  })(); }, [loaded, canEdit]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Build the customer universe ──
  const jobMap = useMemo(() => { const m = {}; (jobs||[]).forEach(j => { const k = stripCP(j.displayDuc || j.duc); if (k) m[k] = j; }); return m; }, [jobs]);

  const universe = useMemo(() => {
    const U = {};
    const get = (name) => { const n = canon(name); if (!n) return null; if (!U[n]) U[n] = { name:n, rev:0, ducs:{}, lastJob:null, lastTouch:null, nextDue:null, contacts:0, prospect:null }; return U[n]; };
    revInput.forEach(e => {
      const u = get(e.customer); if (!u) return; const k = stripCP(e.duc); const amt = _num(e.invAmnt); const d = toISO(e.invDate || e.pullDate);
      u.rev += amt;
      if (k) { const r = u.ducs[k] = u.ducs[k] || { duc:k, amt:0, last:null, pm:e.pm||'' }; r.amt += amt; if (d && (!r.last || d > r.last)) r.last = d; }
      if (d && (!u.lastJob || d > u.lastJob)) u.lastJob = d;
    });
    costRows.forEach(r => { const u = get(r.customer); if (!u) return; const k = stripCP(r.duc); if (k && !u.ducs[k]) u.ducs[k] = { duc:k, amt:0, last:null, pm:r.pm||'', desc:r.desc||r.job||'' }; });
    (jobs||[]).forEach(j => { const u = get(j.cust || j.customer); if (!u) return; const k = stripCP(j.displayDuc || j.duc); if (k && !u.ducs[k]) u.ducs[k] = { duc:k, amt:0, last:null, pm:j.pm||'' }; });
    prospects.forEach(p => { const u = get(p.name); if (u && !Object.keys(u.ducs).length) u.prospect = p; });
    contacts.forEach(c => { const u = get(c.customer); if (u) u.contacts++; });
    activity.forEach(a => {
      const u = get(a.customer); if (!u) return;
      if (a.date && (!u.lastTouch || a.date > u.lastTouch)) u.lastTouch = a.date;
      if (a.nextDate && !a.done && (!u.nextDue || a.nextDate < u.nextDue)) u.nextDue = a.nextDate;
    });
    return U;
  }, [revInput, costRows, jobs, prospects, contacts, activity, canon]);

  const today = todayISO(), soon = addDaysISO(today, 7), yearAgo = addDaysISO(today, -365);
  const list = useMemo(() => {
    let L = Object.values(universe).map(u => ({ ...u, jobs:Object.keys(u.ducs).length }));
    if (filter === 'customers') L = L.filter(u => u.jobs > 0);
    if (filter === 'prospects') L = L.filter(u => u.jobs === 0);
    if (filter === 'due') L = L.filter(u => u.nextDue && u.nextDue <= soon);
    if (filter === 'winback') L = L.filter(u => u.jobs > 0 && (!u.lastJob || u.lastJob < yearAgo) && (!u.lastTouch || daysSince(u.lastTouch) > 90));
    const t = q.trim().toLowerCase();
    if (t) L = L.filter(u => u.name.toLowerCase().includes(t) || contacts.some(c => c.customer === u.name && [c.name, c.email, c.phone].some(x => String(x||'').toLowerCase().includes(t))));
    const s = { rev:(a,b) => b.rev - a.rev, lastJob:(a,b) => String(b.lastJob||'').localeCompare(String(a.lastJob||'')), lastTouch:(a,b) => String(b.lastTouch||'').localeCompare(String(a.lastTouch||'')), name:(a,b) => a.name.localeCompare(b.name) }[sortBy];
    return L.sort(s);
  }, [universe, filter, q, sortBy, contacts, soon, yearAgo]);

  const dueList = useMemo(() => activity.filter(a => a.nextDate && !a.done && a.nextDate <= soon).sort((a,b) => a.nextDate.localeCompare(b.nextDate)), [activity, soon]);
  const counts = useMemo(() => { const v = Object.values(universe); return { all:v.length, customers:v.filter(u => Object.keys(u.ducs).length).length, prospects:v.filter(u => !Object.keys(u.ducs).length).length }; }, [universe]);

  const cur = sel ? universe[sel] : null;
  const curContacts = sel ? contacts.filter(c => c.customer === sel) : [];
  const curActivity = sel ? activity.filter(a => a.customer === sel).sort((a,b) => String(b.date).localeCompare(String(a.date)) || String(b.at).localeCompare(String(a.at))) : [];
  const curJobs = cur ? Object.values(cur.ducs).map(r => { const j = jobMap[r.duc] || {}; return { ...r, desc:j.desc || r.desc || '', city:j.city || '', stat:j.stat || (r.last ? 'Invoiced' : ''), pm:j.pm || r.pm || '' }; }).sort((a,b) => String(b.last||'').localeCompare(String(a.last||''))) : [];

  // ── Actions ──
  const addProspect = async () => {
    const name = canon(pForm.name); if (!name) return;
    if (universe[name]) { setSel(name); setShowAddProspect(false); setPForm({ name:'', city:'', source:'' }); setMsg(`${name} is already on file — opened it.`); return; }
    await mutate(KEY_PROSPECTS, b => [...b, { id:newId('p'), name, city:pForm.city.trim(), source:pForm.source.trim(), by, at:new Date().toISOString() }], setProspects);
    setSel(name); setShowAddProspect(false); setPForm({ name:'', city:'', source:'' }); setMsg(`Added prospect ${name}.`);
  };
  const removeProspect = async (name) => {
    if (!window.confirm(`Remove prospect "${name}"? Contacts and activity stay on file.`)) return;
    await mutate(KEY_PROSPECTS, b => b.filter(p => canon(p.name) !== name), setProspects);
  };
  const saveContact = async () => {
    if (!cForm || !(cForm.name || cForm.email || cForm.phone)) return;
    const rec = { ...cForm, customer:sel, phone:String(cForm.phone||'').replace(/\D/g,'').slice(0,10) || cForm.phone || '' };
    if (rec.id) await mutate(KEY_CONTACTS, b => b.map(c => c.id === rec.id ? { ...c, ...rec, editedBy:by, editedAt:new Date().toISOString() } : c), setContacts);
    else await mutate(KEY_CONTACTS, b => [...b, { ...rec, id:newId('c'), by, at:new Date().toISOString() }], setContacts);
    setCForm(null);
  };
  const deleteContact = async (c) => { if (!window.confirm(`Delete contact ${c.name || c.email}?`)) return; await mutate(KEY_CONTACTS, b => b.filter(x => x.id !== c.id), setContacts); };
  const logActivity = async () => {
    if (!aForm.note.trim() && !aForm.nextDate) return;
    await mutate(KEY_ACTIVITY, b => [...b, { ...aForm, note:aForm.note.trim(), nextNote:aForm.nextNote.trim(), id:newId('a'), customer:sel, done:false, by, at:new Date().toISOString() }], setActivity);
    setAForm({ type:'Call', date:todayISO(), contactId:'', note:'', nextDate:'', nextNote:'' });
  };
  const toggleDone = async (a) => mutate(KEY_ACTIVITY, b => b.map(x => x.id === a.id ? { ...x, done:!x.done, doneBy:by, doneAt:new Date().toISOString() } : x), setActivity);
  const deleteActivity = async (a) => { if (!window.confirm('Delete this activity entry?')) return; await mutate(KEY_ACTIVITY, b => b.filter(x => x.id !== a.id), setActivity); };

  // ── CSV import / export ──
  const onFile = async (file) => {
    if (!file) return;
    const rows = parseCSV(await file.text());
    if (rows.length < 2) { setMsg('That file has no data rows.'); return; }
    const hdr = rows[0].map(h => String(h).trim().toLowerCase());
    const col = (re) => hdr.findIndex(h => re.test(h));
    const iCo = col(/^(customer|company|account|organization|org|business)/), iFirst = col(/^first/), iLast = col(/^last/);
    const iName = col(/^(contact|name|full ?name|contact ?name)$/), iTitle = col(/title|position|role/), iPhone = col(/phone|mobile|cell/);
    const iEmail = col(/e-?mail/), iCity = col(/^city/), iNotes = col(/note|comment/);
    if (iCo < 0) { setMsg('Could not find a Company / Customer column in the header row.'); return; }
    const exists = new Set(contacts.map(c => `${c.customer}|${String(c.email||'').toLowerCase()}|${String(c.name||'').toLowerCase()}`));
    const known = new Set(Object.keys(universe));
    const outC = [], outP = {}; let skipped = 0;
    rows.slice(1).forEach(r => {
      const g = i => i >= 0 ? String(r[i] ?? '').trim() : '';
      const company = canon(g(iCo)); if (!company) { skipped++; return; }
      const name = g(iName) || [g(iFirst), g(iLast)].filter(Boolean).join(' ');
      const email = g(iEmail), phone = g(iPhone);
      if (!known.has(company) && !outP[company]) outP[company] = { id:newId('p'), name:company, city:g(iCity), source:'CSV import', by, at:new Date().toISOString() };
      if (!(name || email || phone)) return;
      const key = `${company}|${email.toLowerCase()}|${name.toLowerCase()}`;
      if (exists.has(key)) { skipped++; return; }
      exists.add(key);
      outC.push({ id:newId('c'), customer:company, name, title:g(iTitle), phone:phone.replace(/\D/g,'').slice(0,10) || phone, email, notes:g(iNotes), by, at:new Date().toISOString(), src:'import' });
    });
    setImp({ contacts:outC, prospects:Object.values(outP), skipped, file:file.name });
  };
  const commitImport = async () => {
    if (!imp) return;
    if (imp.prospects.length) await mutate(KEY_PROSPECTS, b => { const have = new Set(b.map(p => canon(p.name))); return [...b, ...imp.prospects.filter(p => !have.has(p.name))]; }, setProspects);
    if (imp.contacts.length) await mutate(KEY_CONTACTS, b => [...b, ...imp.contacts], setContacts);
    setMsg(`Imported ${imp.contacts.length} contact${imp.contacts.length===1?'':'s'} and ${imp.prospects.length} new prospect${imp.prospects.length===1?'':'s'} from ${imp.file}.`);
    setImp(null);
  };
  const exportContacts = () => downloadCSV(`DUC-CRM-contacts-${todayISO()}.csv`, [
    ['Company','Name','Title','Phone','Email','Notes','Jobs on file','Last job','Last touch'],
    ...contacts.slice().sort((a,b) => String(a.customer).localeCompare(String(b.customer))).map(c => { const u = universe[c.customer] || {}; return [c.customer, c.name, c.title, fmtPhone(c.phone), c.email, c.notes, Object.keys(u.ducs||{}).length, fmtDate(u.lastJob), fmtDate(u.lastTouch)]; }),
  ]);
  const template = () => downloadCSV('DUC-CRM-import-template.csv', [['Company','First Name','Last Name','Title','Phone','Email','City','Notes'], ['Example Construction','Jane','Smith','Project Manager','9725551234','jane@example.com','Dallas','Met at TXUC']]);

  if (!loaded) return <div style={{ color:K.muted, padding:20, fontSize:12 }}>Loading CRM…</div>;

  const pill = (id, label, n) => (
    <button key={id} onClick={() => setFilter(id)} style={{ ...btn, background: filter===id ? K.accent : K.cardHi, color: filter===id ? '#0A0E1A' : K.text, border:`1px solid ${filter===id ? K.accent : K.line}` }}>
      {label}{n != null ? ` · ${n}` : ''}
    </button>
  );

  return (
    <div style={{ color:K.text, fontFamily:'Calibri, Arial, sans-serif', ...(embedded ? {} : { background:K.bg, padding:16, borderRadius:14, minHeight:'70vh' }) }}>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap', marginBottom:12 }}>
        <div style={{ fontSize: embedded ? 14 : 20, fontWeight:900 }}>CRM <span style={{ color:K.accent }}>· Customers & Contacts</span></div>
        <div style={{ fontSize:11, color:K.muted }}>{counts.customers} customers · {counts.prospects} prospects · {contacts.length} contacts</div>
        <div style={{ flex:1 }} />
        {canEdit && <button style={btnGold} onClick={() => setShowAddProspect(v => !v)}>+ Prospect</button>}
        {canEdit && <label style={{ ...btn, display:'inline-block' }}>Import CSV<input type="file" accept=".csv,text/csv" style={{ display:'none' }} onChange={e => { onFile(e.target.files[0]); e.target.value=''; }} /></label>}
        {canEdit && <button style={btn} onClick={template}>Template</button>}
        <button style={btn} onClick={exportContacts} disabled={!contacts.length}>Export</button>
      </div>
      {msg && <div style={{ fontSize:11.5, color:K.green, marginBottom:8, cursor:'pointer' }} onClick={() => setMsg('')}>{msg} <span style={{ color:K.muted }}>✕</span></div>}

      {showAddProspect && canEdit && (
        <div style={{ ...card, marginBottom:12, display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
          <input style={{ ...inp, flex:'2 1 200px' }} placeholder="Company name" value={pForm.name} onChange={e => setPForm({ ...pForm, name:e.target.value })} list="crmNames" />
          <datalist id="crmNames">{Object.keys(universe).sort().map(n => <option key={n} value={n} />)}</datalist>
          <input style={{ ...inp, flex:'1 1 120px' }} placeholder="City" value={pForm.city} onChange={e => setPForm({ ...pForm, city:e.target.value })} />
          <input style={{ ...inp, flex:'1 1 160px' }} placeholder="Source (referral, bid list, show…)" value={pForm.source} onChange={e => setPForm({ ...pForm, source:e.target.value })} />
          <button style={btnGold} onClick={addProspect}>Add</button>
          <button style={btn} onClick={() => setShowAddProspect(false)}>Cancel</button>
        </div>
      )}

      {imp && (
        <div style={{ ...card, marginBottom:12, borderColor:K.accent }}>
          <div style={{ fontSize:13, fontWeight:900, marginBottom:6 }}>Import preview · {imp.file}</div>
          <div style={{ fontSize:12, color:K.muted, marginBottom:8 }}>
            <b style={{ color:K.text }}>{imp.contacts.length}</b> new contacts · <b style={{ color:K.text }}>{imp.prospects.length}</b> companies not on file yet (added as prospects) · {imp.skipped} skipped (duplicate or no company)
          </div>
          <div style={{ maxHeight:160, overflowY:'auto', fontSize:11.5, marginBottom:8 }}>
            {imp.contacts.slice(0,50).map(c => <div key={c.id} style={{ padding:'2px 0', borderTop:`1px solid ${K.line}55` }}><b>{c.customer}</b> — {c.name || '(no name)'}{c.title ? `, ${c.title}` : ''} {c.email ? `· ${c.email}` : ''} {c.phone ? `· ${fmtPhone(c.phone)}` : ''}</div>)}
            {imp.contacts.length > 50 && <div style={{ color:K.muted }}>…and {imp.contacts.length - 50} more</div>}
          </div>
          <button style={btnGold} onClick={commitImport}>Import</button> <button style={btn} onClick={() => setImp(null)}>Cancel</button>
        </div>
      )}

      {/* Follow-ups due */}
      {dueList.length > 0 && (
        <div style={{ ...card, marginBottom:12 }}>
          <div style={{ ...lbl, marginBottom:6 }}>Follow-ups due · next 7 days</div>
          <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
            {dueList.slice(0,12).map(a => { const over = a.nextDate < today; return (
              <div key={a.id} style={{ display:'flex', alignItems:'center', gap:8, fontSize:12 }}>
                <span style={{ flex:'0 0 64px', fontWeight:800, color: over ? K.red : a.nextDate === today ? K.amber : K.text }}>{over ? 'LATE ' : ''}{fmtDate(a.nextDate)}</span>
                <span style={{ flex:'0 1 220px', fontWeight:800, cursor:'pointer', color:K.accent, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} onClick={() => setSel(a.customer)}>{a.customer}</span>
                <span style={{ flex:1, color:K.muted, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{a.nextNote || a.note}</span>
                {canEdit && <button style={{ ...btn, padding:'3px 8px', fontSize:10.5 }} onClick={() => toggleDone(a)}>✓ Done</button>}
              </div>
            ); })}
          </div>
        </div>
      )}

      <div style={{ display:'flex', gap:12, flexWrap:'wrap', alignItems:'flex-start' }}>
        {/* ── List ── */}
        <div style={{ ...card, flex:'1 1 360px', minWidth:0, padding:10 }}>
          <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:8 }}>
            {pill('all', 'All', counts.all)}{pill('customers', 'Customers', counts.customers)}{pill('prospects', 'Prospects', counts.prospects)}
            {pill('due', 'Follow-up due')}{pill('winback', 'Win-back')}
          </div>
          <div style={{ display:'flex', gap:6, marginBottom:8 }}>
            <input style={{ ...inp, flex:1 }} placeholder="Search company, contact, email, phone…" value={q} onChange={e => setQ(e.target.value)} />
            <select style={inp} value={sortBy} onChange={e => setSortBy(e.target.value)}>
              <option value="rev">Revenue</option><option value="lastJob">Last job</option><option value="lastTouch">Last touch</option><option value="name">Name</option>
            </select>
          </div>
          {filter === 'winback' && <div style={{ fontSize:10.5, color:K.muted, marginBottom:6 }}>Past customers with no invoice in 12 months and no touch in 90 days.</div>}
          <div style={{ maxHeight:620, overflowY:'auto' }}>
            {list.length ? list.map(u => (
              <div key={u.name} onClick={() => { setSel(u.name); setCForm(null); }}
                style={{ padding:'7px 8px', borderRadius:8, cursor:'pointer', background: sel === u.name ? K.cardHi : 'transparent', borderLeft:`3px solid ${sel === u.name ? K.accent : 'transparent'}`, marginBottom:1 }}>
                <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                  <span style={{ flex:1, minWidth:0, fontSize:12.5, fontWeight:800, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{u.name}</span>
                  {u.jobs === 0 && <span style={{ fontSize:9.5, fontWeight:900, color:K.blue, border:`1px solid ${K.blue}`, borderRadius:4, padding:'0 4px' }}>PROSPECT</span>}
                  {u.nextDue && u.nextDue <= soon && <span style={{ fontSize:9.5, fontWeight:900, color: u.nextDue < today ? K.red : K.amber }}>● {fmtDate(u.nextDue)}</span>}
                  {u.rev > 0 && <span style={{ fontSize:12, fontWeight:800 }}>{money(u.rev)}</span>}
                </div>
                <div style={{ fontSize:10.5, color:K.muted, marginTop:1 }}>
                  {u.jobs} job{u.jobs === 1 ? '' : 's'} · last job {fmtDate(u.lastJob)} · last touch {u.lastTouch ? `${fmtDate(u.lastTouch)} (${daysSince(u.lastTouch)}d)` : 'never'}{u.contacts ? ` · ${u.contacts} contact${u.contacts === 1 ? '' : 's'}` : ''}
                </div>
              </div>
            )) : <div style={{ fontSize:12, color:K.muted, padding:8 }}>Nothing matches.</div>}
          </div>
        </div>

        {/* ── Detail ── */}
        <div style={{ flex:'2 1 480px', minWidth:0, display:'flex', flexDirection:'column', gap:12 }}>
          {!cur ? (
            <div style={{ ...card, color:K.muted, fontSize:12.5 }}>Pick a customer or prospect on the left to see contacts, activity, and past jobs.</div>
          ) : (<>
            <div style={card}>
              <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                <div style={{ fontSize:17, fontWeight:900 }}>{cur.name}</div>
                {Object.keys(cur.ducs).length === 0
                  ? <span style={{ fontSize:10, fontWeight:900, color:K.blue, border:`1px solid ${K.blue}`, borderRadius:4, padding:'1px 5px' }}>PROSPECT</span>
                  : <span style={{ fontSize:10, fontWeight:900, color:K.green, border:`1px solid ${K.green}`, borderRadius:4, padding:'1px 5px' }}>CUSTOMER</span>}
                <div style={{ flex:1 }} />
                {canEdit && cur.prospect && <button style={{ ...btn, fontSize:10.5, padding:'3px 8px', color:K.red }} onClick={() => removeProspect(cur.name)}>Remove prospect</button>}
              </div>
              {cur.prospect && (cur.prospect.city || cur.prospect.source) && <div style={{ fontSize:11, color:K.muted, marginTop:3 }}>{[cur.prospect.city, cur.prospect.source && `Source: ${cur.prospect.source}`].filter(Boolean).join(' · ')}</div>}
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(110px, 1fr))', gap:8, marginTop:10 }}>
                {[['Revenue on file', money(cur.rev)], ['Jobs', Object.keys(cur.ducs).length], ['Last job', fmtDate(cur.lastJob)], ['Last touch', cur.lastTouch ? `${fmtDate(cur.lastTouch)} · ${daysSince(cur.lastTouch)}d` : 'never'], ['Next follow-up', fmtDate(cur.nextDue)]].map(([l, v]) => (
                  <div key={l} style={{ background:K.cardHi, borderRadius:8, padding:'7px 9px' }}><div style={lbl}>{l}</div><div style={{ fontSize:14, fontWeight:900, marginTop:2 }}>{v}</div></div>
                ))}
              </div>
            </div>

            {/* Contacts */}
            <div style={card}>
              <div style={{ display:'flex', alignItems:'center', marginBottom:6 }}>
                <div style={lbl}>Contacts · {curContacts.length}</div><div style={{ flex:1 }} />
                {canEdit && !cForm && <button style={{ ...btn, padding:'3px 9px', fontSize:10.5 }} onClick={() => setCForm({ name:'', title:'', phone:'', email:'', notes:'' })}>+ Contact</button>}
              </div>
              {cForm && canEdit && (
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(150px, 1fr))', gap:6, marginBottom:8, padding:8, background:K.cardHi, borderRadius:8 }}>
                  <input style={inp} placeholder="Name" value={cForm.name} onChange={e => setCForm({ ...cForm, name:e.target.value })} />
                  <input style={inp} placeholder="Title" value={cForm.title} onChange={e => setCForm({ ...cForm, title:e.target.value })} />
                  <input style={inp} placeholder="Phone" inputMode="tel" value={cForm.phone} onChange={e => setCForm({ ...cForm, phone:e.target.value })} />
                  <input style={inp} placeholder="Email" value={cForm.email} onChange={e => setCForm({ ...cForm, email:e.target.value })} />
                  <input style={{ ...inp, gridColumn:'1 / -1' }} placeholder="Notes" value={cForm.notes} onChange={e => setCForm({ ...cForm, notes:e.target.value })} />
                  <div style={{ gridColumn:'1 / -1', display:'flex', gap:6 }}><button style={btnGold} onClick={saveContact}>{cForm.id ? 'Save' : 'Add contact'}</button><button style={btn} onClick={() => setCForm(null)}>Cancel</button></div>
                </div>
              )}
              {curContacts.length ? curContacts.map(c => (
                <div key={c.id} style={{ display:'flex', alignItems:'flex-start', gap:8, padding:'6px 0', borderTop:`1px solid ${K.line}55`, fontSize:12 }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div><b>{c.name || '(no name)'}</b>{c.title ? <span style={{ color:K.muted }}> · {c.title}</span> : null}</div>
                    <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginTop:1 }}>
                      {c.phone && <a href={`tel:${String(c.phone).replace(/\D/g,'')}`} style={{ color:K.accent, textDecoration:'none' }}>{fmtPhone(c.phone)}</a>}
                      {c.email && <a href={`mailto:${c.email}`} style={{ color:K.blue, textDecoration:'none' }}>{c.email}</a>}
                    </div>
                    {c.notes && <div style={{ color:K.muted, fontSize:11, marginTop:1 }}>{c.notes}</div>}
                  </div>
                  {canEdit && <><button style={{ ...btn, padding:'2px 7px', fontSize:10 }} onClick={() => setCForm({ ...c, phone:fmtPhone(c.phone) })}>Edit</button><button style={{ ...btn, padding:'2px 7px', fontSize:10, color:K.red }} onClick={() => deleteContact(c)}>✕</button></>}
                </div>
              )) : !cForm && <div style={{ fontSize:11.5, color:K.muted }}>No contacts yet.</div>}
            </div>

            {/* Activity */}
            <div style={card}>
              <div style={{ ...lbl, marginBottom:6 }}>Activity · {curActivity.length}</div>
              {canEdit && (
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(130px, 1fr))', gap:6, marginBottom:10, padding:8, background:K.cardHi, borderRadius:8 }}>
                  <select style={inp} value={aForm.type} onChange={e => setAForm({ ...aForm, type:e.target.value })}>{ACT_TYPES.map(t => <option key={t}>{t}</option>)}</select>
                  <input style={inp} type="date" value={aForm.date} onChange={e => setAForm({ ...aForm, date:e.target.value })} />
                  <select style={inp} value={aForm.contactId} onChange={e => setAForm({ ...aForm, contactId:e.target.value })}>
                    <option value="">— contact —</option>{curContacts.map(c => <option key={c.id} value={c.id}>{c.name || c.email}</option>)}
                  </select>
                  <textarea style={{ ...inp, gridColumn:'1 / -1', resize:'vertical' }} rows={2} placeholder="What happened? (called about the Ennis bid, sent rates, etc.)" value={aForm.note} onChange={e => setAForm({ ...aForm, note:e.target.value })} />
                  <div style={{ gridColumn:'1 / -1', display:'flex', gap:6, flexWrap:'wrap', alignItems:'center' }}>
                    <span style={{ fontSize:11, color:K.muted }}>Follow up</span>
                    <input style={inp} type="date" value={aForm.nextDate} onChange={e => setAForm({ ...aForm, nextDate:e.target.value })} />
                    {[7, 14, 30].map(n => <button key={n} style={{ ...btn, padding:'3px 7px', fontSize:10 }} onClick={() => setAForm({ ...aForm, nextDate:addDaysISO(todayISO(), n) })}>+{n}d</button>)}
                    <input style={{ ...inp, flex:1, minWidth:140 }} placeholder="Follow-up note" value={aForm.nextNote} onChange={e => setAForm({ ...aForm, nextNote:e.target.value })} />
                    <button style={btnGold} onClick={logActivity}>Log</button>
                  </div>
                </div>
              )}
              {curActivity.length ? curActivity.map(a => { const ct = contacts.find(c => c.id === a.contactId); return (
                <div key={a.id} style={{ display:'flex', gap:8, padding:'6px 0', borderTop:`1px solid ${K.line}55`, fontSize:12 }}>
                  <span style={{ flex:'0 0 58px', fontSize:10, fontWeight:900, color:ACT_CLR[a.type] || K.muted, textTransform:'uppercase' }}>{a.type}</span>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ color:K.muted, fontSize:10.5 }}>{fmtDate(a.date)}{ct ? ` · ${ct.name}` : ''}{a.by ? ` · by ${a.by}` : ''}</div>
                    {a.note && <div style={{ whiteSpace:'pre-wrap' }}>{a.note}</div>}
                    {a.nextDate && (
                      <div style={{ fontSize:11, marginTop:2, color: a.done ? K.muted : (a.nextDate < today ? K.red : K.amber), textDecoration: a.done ? 'line-through' : 'none' }}>
                        ↳ Follow up {fmtDate(a.nextDate)}{a.nextNote ? ` — ${a.nextNote}` : ''}
                        {canEdit && <button style={{ ...btn, padding:'1px 6px', fontSize:9.5, marginLeft:6 }} onClick={() => toggleDone(a)}>{a.done ? 'Reopen' : '✓ Done'}</button>}
                      </div>
                    )}
                  </div>
                  {canEdit && <button style={{ ...btn, padding:'2px 7px', fontSize:10, color:K.red, alignSelf:'flex-start' }} onClick={() => deleteActivity(a)}>✕</button>}
                </div>
              ); }) : <div style={{ fontSize:11.5, color:K.muted }}>No activity logged yet.</div>}
            </div>

            {/* Past jobs */}
            <div style={card}>
              <div style={{ ...lbl, marginBottom:6 }}>Jobs with us · {curJobs.length}</div>
              {curJobs.length ? (
                <div style={{ overflowX:'auto' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:11.5 }}>
                    <thead><tr style={{ color:K.muted, textAlign:'left' }}>{['Job #','Description','City','PM','Status','Invoiced','Last inv.'].map(h => <th key={h} style={{ padding:'4px 6px', fontSize:10, textTransform:'uppercase', borderBottom:`1px solid ${K.line}` }}>{h}</th>)}</tr></thead>
                    <tbody>{curJobs.map(j => (
                      <tr key={j.duc} style={{ borderTop:`1px solid ${K.line}55` }}>
                        <td style={{ padding:'4px 6px', fontWeight:800, whiteSpace:'nowrap' }}>{j.duc}</td>
                        <td style={{ padding:'4px 6px', maxWidth:280, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={j.desc}>{j.desc || '—'}</td>
                        <td style={{ padding:'4px 6px', whiteSpace:'nowrap' }}>{j.city || '—'}</td>
                        <td style={{ padding:'4px 6px' }}>{j.pm || '—'}</td>
                        <td style={{ padding:'4px 6px', whiteSpace:'nowrap' }}>{j.stat || '—'}</td>
                        <td style={{ padding:'4px 6px', textAlign:'right', fontWeight:800 }}>{j.amt ? money(j.amt) : '—'}</td>
                        <td style={{ padding:'4px 6px', whiteSpace:'nowrap' }}>{fmtDate(j.last)}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              ) : <div style={{ fontSize:11.5, color:K.muted }}>No jobs on file — prospect.</div>}
            </div>
          </>)}
        </div>
      </div>
    </div>
  );
}
